// src/lib/mcpConfig.ts
//
// Configurable MCP base URL resolution and the canonical server config shape.
//
// This module defines the shared `McpServerConfig` type that both `mcpConfig.ts`
// and `ideInstall.ts` re-use, plus the pure helpers that resolve the configurable
// MCP base URL (`resolveMcpBaseUrl`) and build the canonical server config
// (`buildServerConfig`).

/** Canonical, transport-agnostic description of the SDLC MCP server. */
export interface McpServerConfig {
  /** Resolved `/mcp` endpoint. */
  url: string
  /** Logical server name registered in the IDE. Always `'sdlc-platform'`. */
  serverName: string
}

/** Documented fallback MCP base URL used when `VITE_MCP_URL` is unset/empty. */
const DEFAULT_BASE_URL = 'https://sdlc-mcp.dev.wenni.io/mcp'

/** Upper bound on the accepted `VITE_MCP_URL` length, in characters. */
const MAX_URL_LENGTH = 2048

/** Logical server name registered in the IDE. */
const SERVER_NAME = 'sdlc-platform'

/**
 * Resolve the configurable MCP base URL.
 *
 * Returns `VITE_MCP_URL` when it is set, non-whitespace, and at most 2048
 * characters, with any trailing slash characters stripped. Otherwise returns
 * the documented {@link DEFAULT_BASE_URL}.
 *
 * When `VITE_MCP_URL` is set but is not an absolute `https://` URL, a
 * development-time warning is emitted and the configured value is returned
 * unchanged (it is not substituted by the default).
 *
 * Pure and deterministic for a given environment; no mutation and no side
 * effects beyond the gated development warning.
 */
export function resolveMcpBaseUrl(): string {
  const configured = import.meta.env.VITE_MCP_URL

  if (typeof configured !== 'string' || configured.trim() === '' || configured.length > MAX_URL_LENGTH) {
    return DEFAULT_BASE_URL
  }

  const trimmed = stripTrailingSlashes(configured)

  if (!trimmed.startsWith('https://')) {
    if (import.meta.env.DEV) {
      console.warn(`VITE_MCP_URL is not an absolute https:// URL: ${configured}`)
    }
    return configured
  }

  return trimmed
}

/**
 * Build the canonical, transport-agnostic SDLC MCP server config.
 *
 * Pure and total: returns `{ url: baseUrl, serverName: 'sdlc-platform' }`.
 */
export function buildServerConfig(baseUrl: string): McpServerConfig {
  return { url: baseUrl, serverName: SERVER_NAME }
}

/** Remove any trailing slash characters from a string. */
function stripTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, '')
}
